// What a campaign did.
//
// The counts come from the server already classified, so the browser and the
// assistant cannot disagree about how a campaign went.
//
// "Reached" means Retell's record says a person was on the line -- not that
// the dial succeeded, and not that the call ran for a while. A voicemail the
// agent talks to for 68 seconds looks identical to a conversation if you only
// measure length, so only `reached` is coloured as success and `placed`
// (dialled, vendor record not in yet) is explicitly not a conversation.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { campaignApi, CampaignCall, CampaignResults as Results } from '../services/campaignApi';

interface Props {
  campaignId: string;
}

// Three states, because three is what the database supports. An earlier
// version coloured a "reached" bucket green — but whether anyone answered is
// not recorded anywhere, so that green was decoration over a guess.
const TONE: Record<CampaignCall['outcome'],
  'success' | 'info' | 'warning' | 'error' | 'default'> = {
  reached: 'success',
  answered_brief: 'warning',
  voicemail: 'info',
  no_answer: 'default',
  in_progress: 'warning',
  failed: 'error',
  placed: 'default',
  not_yet: 'default',
};

const ORDER: CampaignCall['outcome'][] = [
  'reached', 'voicemail', 'answered_brief', 'no_answer',
  'in_progress', 'failed', 'placed', 'not_yet',
];

const LABEL: Record<CampaignCall['outcome'], string> = {
  reached: 'Spoke with someone',
  voicemail: 'Voicemail',
  answered_brief: 'Picked up, hung up',
  no_answer: 'No answer',
  in_progress: 'On the call now',
  failed: 'Could not be placed',
  placed: 'Awaiting outcome',
  not_yet: 'Not called yet',
};

const duration = (seconds: number | null) => {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
};

const CampaignResults: React.FC<Props> = ({ campaignId }) => {
  const [results, setResults] = useState<Results | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setResults(await campaignApi.getCampaignResults(campaignId));
    } catch (e: any) {
      // Said out loud. The whole reason this component exists is that the
      // previous version swallowed this into console.error and rendered zeroes.
      setError(e?.message || 'Could not load the results for this campaign.');
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  if (loading && !results) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }} action={
        <Button color="inherit" size="small" onClick={load} startIcon={<RefreshIcon />}>
          Try again
        </Button>
      }>
        <AlertTitle>The results could not be loaded</AlertTitle>
        {error} These are not zero results — they are no results.
      </Alert>
    );
  }

  if (!results) return null;

  if (results.total_scheduled === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        {results.summary}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <Typography
          sx={{ fontSize: 40, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}
          color={results.outcome_recorded ? 'success.main' : 'text.secondary'}
        >
          {results.outcome_recorded ? results.reached : results.placed}
        </Typography>
        <Box>
          <Typography variant="body1">
            of {results.total_scheduled}{' '}
            {results.outcome_recorded ? 'reached someone' : 'call(s) placed'}
          </Typography>
          <Tooltip
            arrow
            title={results.outcome_recorded
              ? 'A call where Retell\u2019s record says a person was on the line. Voicemails are counted separately, however long they ran.'
              : 'The dial succeeded. What happened on the call has not come back from Retell yet, so nobody is counted as reached.'}
          >
            <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
              what this counts
            </Typography>
          </Tooltip>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={load} startIcon={<RefreshIcon />} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {/* Said on screen, not just in the payload: while this is false the zero
          next to "reached" means "not known", not "nobody answered". */}
      {!results.outcome_recorded && results.placed > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          These calls were placed, but what happened on them has not come back
          from Retell yet. Refresh in a moment.
        </Alert>
      )}
      {results.calls_not_analysed > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {results.calls_not_analysed} more call(s) were not looked up in this
          report.
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {results.summary}
      </Typography>

      <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 3 }}>
        {ORDER.filter((o) => results.counts[o] > 0).map((o) => (
          <Chip
            key={o}
            size="small"
            color={TONE[o] === 'default' ? undefined : TONE[o]}
            variant={TONE[o] === 'default' ? 'outlined' : 'filled'}
            label={`${results.counts[o]} ${LABEL[o].toLowerCase()}`}
          />
        ))}
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Patient</TableCell>
              <TableCell>Number</TableCell>
              <TableCell>Outcome</TableCell>
              <TableCell align="right">Length</TableCell>
              <TableCell>How it went</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.calls.map((c) => (
              <TableRow
                key={c.scheduled_call_id}
                sx={{ bgcolor: (t) => c.outcome === 'reached'
                  ? alpha(t.palette.success.main, 0.06) : 'inherit' }}
              >
                {/* A number here instead of a name is not a missing name: the
                    agent greets by first name and falls back to the number at
                    launch, so showing the number as a name looks like a bug. */}
                <TableCell>{c.name ?? <em>not recorded</em>}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.to_number}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={TONE[c.outcome] === 'default' ? undefined : TONE[c.outcome]}
                    variant={TONE[c.outcome] === 'default' ? 'outlined' : 'filled'}
                    label={c.outcome_label}
                  />
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {duration(c.duration_seconds)}
                </TableCell>
                <TableCell sx={{ minWidth: 280 }}>
                  {/* The summary is the reason to run a recall campaign: it is
                      the difference between "we called 30 people" and "nine of
                      them booked". */}
                  {c.summary
                    ? <Typography variant="body2">{c.summary}</Typography>
                    : <Typography variant="body2" color="text.secondary">—</Typography>}
                  {c.sentiment && (
                    <Chip size="small" variant="outlined" sx={{ mt: 0.6 }}
                          label={`sounded ${c.sentiment.toLowerCase()}`} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CampaignResults;
